import React, { useState, useEffect } from 'react';
import jsPDF from 'jspdf';
import styles from './Guide.module.css';

const PDFPreview = ({ formData, methodId, detailLevel }) => {
  const [sections, setSections] = useState([]);
  const [orientation, setOrientation] = useState('portrait');

  useEffect(() => {
    import(`./forms/${methodId}/${detailLevel}.js`).then((module) => {
      setSections(module.default);
    });
  }, [methodId, detailLevel]);

  const handleExport = async () => {
    const doc = new jsPDF({ orientation });
    const date = new Date().toLocaleDateString();
    const logo = await toDataURL('/logo512.png');

    let y = 0;
    let pageNumber = 1;
    const contentPages = [];

    const addHeader = () => {
      y = 20;
      doc.addImage(logo, 'PNG', 10, 8, 18, 18);
      doc.setFontSize(16);
      doc.text(`Fiche Projet - Méthode ${methodId.toUpperCase()}`, 105, y, { align: 'center' });
      y += 8;
      doc.setFontSize(11);
      doc.text(`Niveau : ${detailLevel.toUpperCase()}`, 105, y, { align: 'center' });
      y += 6;
      doc.line(10, y + 2, 200, y + 2);
      y += 10;
    };

    const addWatermark = () => {
      doc.setTextColor(210, 210, 210);
      doc.setFontSize(28);
      doc.text('www.clublfit.org', 105, 145, { align: 'center', angle: -20 });
      doc.setTextColor(0, 0, 0);
    };

    const addFooter = (currentPage, totalPages) => {
      doc.setFontSize(9);
      doc.setTextColor(100);
      doc.text(`Page ${currentPage} / ${totalPages}`, 200 - 10, 288, { align: 'right' });
      doc.setTextColor(0);
    };

    const startNewPage = () => {
      if (pageNumber > 1) doc.addPage();
      addHeader();
      addWatermark();
      contentPages.push(pageNumber);
      pageNumber++;
    };

    startNewPage();
    doc.setFontSize(12);

    for (const section of sections) {
      const sectionHeight = 10;
      if (y + sectionHeight > 260) startNewPage();

      doc.setFontSize(14);
      doc.setTextColor(0, 51, 102);
      doc.text(section.title, 10, y);
      y += 6;

      doc.setFontSize(11);
      doc.setTextColor(100);
      if (section.description) {
        const descLines = doc.splitTextToSize(section.description, 180);
        doc.text(descLines, 10, y);
        y += descLines.length * 5.5;
      }

      for (const field of section.fields) {
        const answer = formData[field.id];
        if (typeof answer === 'string' && answer.trim()) {
          const lines = doc.splitTextToSize(answer, 180);
          doc.setTextColor(0);
          doc.setFontSize(11);
          doc.text(`• ${field.label}`, 10, y);
          y += 5;
          doc.setTextColor(50);
          doc.text(lines, 14, y);
          y += lines.length * 5.5 + 3;

          if (y > 260) startNewPage();
        }
      }

      y += 3;
    }

    // Dernière page : signature uniquement
    if (y + 40 < 250) {
      y = 240; // ou 240 pour un compromis
    } else {
      doc.addPage();
      y = 230;
    }
    
    doc.setFontSize(11);
    doc.setTextColor(0);
    doc.line(10, y, 200, y);
    y += 8;
    doc.text('Porteur du projet : ____________________________', 10, y);
    y += 8;
    doc.text('Référent ou encadrant : ________________________', 10, y);
    y += 8;
    doc.text(`Fait à : ______________________     Le : ${date}`, 10, y);
    y += 12;
    doc.text('Signature :', 10, y);
    doc.line(35, y, 100, y);

    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      addFooter(i, totalPages);
    }

    const nomProjet = formData['nom_projet']?.trim().replaceAll(' ', '_') || 'SansNom';
    if (methodId === 'swot') {
      // 🔶 Matrice SWOT visuelle (forces/faiblesses/opportunités/menaces)
      doc.addPage();
      doc.setFontSize(14);
      doc.setTextColor(0, 51, 102);
      doc.text('Synthèse visuelle SWOT', 105, 20, { align: 'center' });
    
      const cellWidth = 95;
      const cellHeight = 60;
      const xLeft = 10;
      const xRight = xLeft + cellWidth;
      const yTop = 30;
      const yBottom = yTop + cellHeight;
    
      doc.setFontSize(12);
      doc.setDrawColor(0);
      doc.setFillColor(230, 240, 255);
      doc.rect(xLeft, yTop - 8, cellWidth, 8, 'F');
      doc.rect(xRight, yTop - 8, cellWidth, 8, 'F');
      doc.setTextColor(0);
      doc.setFontSize(11);
      doc.text('Forces', xLeft + cellWidth / 2, yTop - 2, { align: 'center' });
      doc.text('Faiblesses', xRight + cellWidth / 2, yTop - 2, { align: 'center' });
      doc.setFillColor(230, 240, 255);
      doc.rect(xLeft, yBottom + cellHeight + 4, cellWidth, 8, 'F');
      doc.rect(xRight, yBottom + cellHeight + 4, cellWidth, 8, 'F');
      doc.setTextColor(0);
      doc.setFontSize(11);
      doc.text('Opportunités', xLeft + cellWidth / 2, yBottom + cellHeight + 10, { align: 'center' });
      doc.text('Menaces', xRight + cellWidth / 2, yBottom + cellHeight + 10, { align: 'center' });
    
      doc.rect(xLeft, yTop, cellWidth, cellHeight);
      doc.rect(xRight, yTop, cellWidth, cellHeight);
      doc.rect(xLeft, yBottom, cellWidth, cellHeight);
      doc.rect(xRight, yBottom, cellWidth, cellHeight);
    
      doc.setFontSize(10);
      const wrap = (txt) => doc.splitTextToSize(txt?.trim() || '-', cellWidth - 4);
      doc.text(wrap(formData['force_1'] + '\n' + formData['force_2'] + '\n' + formData['force_3']), xLeft + 2, yTop + 6);
      doc.text(wrap(formData['faiblesse_1'] + '\n' + formData['faiblesse_2'] + '\n' + formData['faiblesse_3']), xRight + 2, yTop + 6);
      doc.text(wrap(formData['opportunite_1'] + '\n' + formData['opportunite_2'] + '\n' + formData['opportunite_3']), xLeft + 2, yBottom + 6);
      doc.text(wrap(formData['menace_1'] + '\n' + formData['menace_2'] + '\n' + formData['menace_3']), xRight + 2, yBottom + 6);
    
      // 🔷 Matrice stratégique SWOT croisée
      doc.addPage();
      doc.setFontSize(14);
      doc.setTextColor(0, 51, 102);
      doc.text('Croisement stratégique SWOT', 105, 20, { align: 'center' });
    
      const y2Top = 30;
      const y2Bottom = y2Top + cellHeight;
      doc.setFontSize(12);
      doc.setFillColor(230, 240, 255);
      doc.rect(xLeft, y2Top - 8, cellWidth, 8, 'F');
      doc.rect(xRight, y2Top - 8, cellWidth, 8, 'F');
      doc.rect(xLeft, y2Bottom + cellHeight + 4, cellWidth, 8, 'F');
      doc.rect(xRight, y2Bottom + cellHeight + 4, cellWidth, 8, 'F');
      doc.setTextColor(0);
      doc.setFontSize(11);
      doc.text('F × O : Stratégie SO', xLeft + cellWidth / 2, y2Top - 2, { align: 'center' });
      doc.text('F × T : Stratégie ST', xRight + cellWidth / 2, y2Top - 2, { align: 'center' });
      doc.text('W × O : Stratégie WO', xLeft + cellWidth / 2, y2Bottom + cellHeight + 10, { align: 'center' });
      doc.text('W × T : Stratégie WT', xRight + cellWidth / 2, y2Bottom + cellHeight + 10, { align: 'center' });
    
      doc.rect(xLeft, y2Top, cellWidth, cellHeight);
      doc.rect(xRight, y2Top, cellWidth, cellHeight);
      doc.rect(xLeft, y2Bottom, cellWidth, cellHeight);
      doc.rect(xRight, y2Bottom, cellWidth, cellHeight);
    
      doc.setFontSize(10);
      doc.text(wrap(formData['strategie_so']), xLeft + 2, y2Top + 6);
      doc.text(wrap(formData['strategie_st']), xRight + 2, y2Top + 6);
      doc.text(wrap(formData['strategie_wo']), xLeft + 2, y2Bottom + 6);
      doc.text(wrap(formData['strategie_wt']), xRight + 2, y2Bottom + 6);
    
      // 🟩 Frise temporelle : court / moyen / long terme
      doc.addPage();
      doc.setFontSize(14);
      doc.setTextColor(0, 51, 102);
      doc.text('Plan d’action priorisé (Timeline)', 105, 20, { align: 'center' });
    
      
    
      const xStart = 20;
      const xMiddle = 105;
      const xEnd = 190;
      const yLine = 60;
    
      doc.setDrawColor(0);
      doc.line(xStart, yLine, xEnd, yLine);
    
      doc.circle(xStart, yLine, 2, 'F');
      doc.circle(xMiddle, yLine, 2, 'F');
      doc.circle(xEnd, yLine, 2, 'F');
    
      doc.setFontSize(11);
      doc.setFillColor(230, 240, 255);
      doc.rect(xStart - 20, yLine + 2, 40, 8, 'F');
      doc.rect(xMiddle - 25, yLine + 2, 50, 8, 'F');
      doc.rect(xEnd - 20, yLine + 2, 40, 8, 'F');
      doc.setTextColor(0);
      doc.setFontSize(11);
      doc.text('Court terme', xStart, yLine + 8, { align: 'center' });
      doc.text('Moyen terme', xMiddle, yLine + 8, { align: 'center' });
      doc.text('Long terme', xEnd, yLine + 8, { align: 'center' });
    
      doc.setFontSize(10);
      doc.setTextColor(50);
      doc.text(wrap(formData['action_court_terme']), xStart - 10, yLine - 25);
      doc.text(wrap(formData['action_moyen_terme']), xMiddle - 40, yLine - 25);
      doc.text(wrap(formData['action_long_terme']), xEnd - 40, yLine - 25);
    }
    
    doc.save(`${nomProjet}_${methodId}_${detailLevel}.pdf`);
  };

  const toDataURL = (url) =>
    fetch(url)
      .then((res) => res.blob())
      .then(
        (blob) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          })
      );

  return (
    <div className={styles['pdf-preview']}>
      <button onClick={handleExport}>📄 Exporter en PDF</button>
      <button onClick={() => setOrientation((o) => (o === 'portrait' ? 'landscape' : 'portrait'))}>
        📐 Mode : {orientation === 'portrait' ? 'Portrait' : 'Paysage'}
      </button>
    </div>
  );
};

export default PDFPreview;
